// Instrument (track) management (§1.8) and score metadata.

import type { Draft } from 'immer';
import { type Pitch, midiOf, spellMidi } from '@simplenotation/theory';
import type { Score, Track, ScoreMeta, TrackBar } from '../model/types';
import { createTrack, emptyTrackBar, newId, cloneJson } from '../model/factories';
import { instrumentDef } from '../model/instruments';
import { timeSigAt } from '../model/query';
import { type EditorState, mutate, withError, focusBeat, refingerBeat, trackKeyAt } from './state';

export function addTrack(st: EditorState, instrument: string, name?: string): EditorState {
  const next = mutate(st, (d) => {
    const t = createTrack(0, timeSigAt(d as Score, 0), { instrument, name });
    t.bars = d.bars.map((_, i) => emptyTrackBar(timeSigAt(d as Score, i)));
    // Avoid duplicate names.
    const names = new Set(d.tracks.map((x) => x.name));
    if (names.has(t.name)) {
      let k = 2;
      while (names.has(`${t.name} ${k}`)) k++;
      t.name = `${t.name} ${k}`;
    }
    d.tracks.push(t as Draft<Track>);
  });
  return focusBeat(next, { track: next.score.tracks.length - 1, bar: st.cursor.bar, voice: 0, beat: 0 });
}

export function removeTrack(st: EditorState, idx: number): EditorState {
  if (st.score.tracks.length <= 1) return withError(st, 'A score needs at least one instrument.');
  const next = mutate(st, (d) => {
    d.tracks.splice(idx, 1);
  });
  const track = Math.min(st.cursor.track, next.score.tracks.length - 1);
  return focusBeat({ ...next, selection: null }, { track, bar: st.cursor.bar, voice: 0, beat: 0 });
}

export function duplicateTrack(st: EditorState, idx: number): EditorState {
  return mutate(st, (d) => {
    const copy = cloneJson(d.tracks[idx] as Track);
    copy.id = newId();
    copy.name = `${copy.name} (copy)`;
    for (const tb of copy.bars) for (const v of tb.voices) for (const b of v) {
      b.id = newId();
      for (const n of b.notes) n.id = newId();
    }
    d.tracks.splice(idx + 1, 0, copy as Draft<Track>);
  });
}

export function moveTrack(st: EditorState, idx: number, dir: -1 | 1): EditorState {
  const to = idx + dir;
  if (to < 0 || to >= st.score.tracks.length) return st;
  const next = mutate(st, (d) => {
    const [t] = d.tracks.splice(idx, 1);
    d.tracks.splice(to, 0, t);
  });
  return { ...next, cursor: { ...next.cursor, track: next.cursor.track === idx ? to : next.cursor.track === to ? idx : next.cursor.track } };
}

export type TrackPatch = Partial<
  Pick<
    Track,
    'name' | 'shortName' | 'instrument' | 'sound' | 'writtenToSounding' | 'clef' | 'tuning' | 'capo' | 'showStaff' | 'showTab' | 'tabStems' | 'visualizations' | 'volume' | 'pan' | 'mute' | 'solo'
  >
>;

/**
 * Update an instrument. Changing the transposition keeps sounding pitches
 * (written notes are respelled); changing tuning or capo keeps pitches and
 * re-fingers the tab.
 */
export function updateTrack(st: EditorState, idx: number, patch: TrackPatch): EditorState {
  const old = st.score.tracks[idx];
  if (!old) return st;
  if (patch.showStaff === false && (patch.showTab ?? old.showTab) === false) return withError(st, 'Show at least staff or tab.');
  return mutate(st, (d) => {
    const t = d.tracks[idx];
    const w2sChanged = patch.writtenToSounding !== undefined && patch.writtenToSounding !== old.writtenToSounding;
    Object.assign(t, patch);
    if (patch.tuning === undefined && 'tuning' in patch) delete t.tuning;
    if (w2sChanged) {
      const delta = old.writtenToSounding - patch.writtenToSounding!;
      t.bars.forEach((tb, bar) => {
        const key = trackKeyAt(d as Score, idx, bar);
        for (const v of tb.voices) for (const b of v) for (const n of b.notes) {
          n.pitch = spellMidi(midiOf(n.pitch as Pitch) + delta, key.fifths);
        }
      });
    }
    const tabChanged =
      w2sChanged ||
      (patch.tuning !== undefined && JSON.stringify(patch.tuning) !== JSON.stringify(old.tuning)) ||
      (patch.capo !== undefined && patch.capo !== old.capo);
    if (tabChanged && t.tuning) {
      t.bars.forEach((tb, bar) => {
        tb.voices.forEach((v, voice) => {
          v.forEach((_, beat) => refingerBeat(d, { track: idx, bar, voice, beat }));
        });
      });
    }
    if (!t.tuning) {
      t.showTab = false;
      for (const tb of t.bars) for (const v of tb.voices) for (const b of v) for (const n of b.notes) {
        delete n.string;
        delete n.fret;
      }
    }
  });
}

/** Switch a track to another instrument type, taking that instrument's defaults. */
export function changeInstrument(st: EditorState, idx: number, instrument: string): EditorState {
  const def = instrumentDef(instrument);
  const old = st.score.tracks[idx];
  const patch: TrackPatch = {
    instrument: def.id,
    sound: def.sound,
    writtenToSounding: def.writtenToSounding,
    clef: def.clef,
    tuning: def.tuning ? [...def.tuning] : undefined,
    capo: def.tuning ? old.capo ?? 0 : undefined,
    showTab: !!def.tuning && (old.showTab || !!def.showTab),
    showStaff: old.showStaff || !def.tuning,
    visualizations: [...(def.visualizations ?? [])],
  };
  if (old.name === instrumentDef(old.instrument).name.replace(/ \(.*\)$/, '')) patch.name = def.name.replace(/ \(.*\)$/, '');
  return updateTrack(st, idx, patch);
}

export function setMeta(st: EditorState, patch: Partial<ScoreMeta>): EditorState {
  return mutate(st, (d) => {
    Object.assign(d.meta, patch);
    for (const k of Object.keys(d.meta) as (keyof ScoreMeta)[]) {
      if (k !== 'title' && !d.meta[k]) delete d.meta[k];
    }
  });
}

/** Replace a track's bars wholesale (used by importers that add a track). */
export function appendTrackFrom(st: EditorState, track: Track): EditorState {
  return mutate(st, (d) => {
    const t = cloneJson(track);
    t.id = newId();
    while (t.bars.length < d.bars.length) t.bars.push(emptyTrackBar(timeSigAt(d as Score, t.bars.length)) as TrackBar);
    t.bars.length = d.bars.length;
    d.tracks.push(t as Draft<Track>);
  });
}
